<template>
  <div class="guide-quick-start">
    <p class="guide-lead">
      Two paths to your first signed document. The Portal Quick Start uses only the
      hosted web UI and takes about three minutes with no code. The API Quick Start
      below drops to the REST API and produces the same result programmatically in about
      five minutes. Use your own email as the signer in either path so you complete the
      ceremony end-to-end and see what a real signer sees.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Portal Quick Start</h2>
    <p>
      Send and sign your first document from the web portal in a handful of clicks.
      Every subscription tier supports this path, including Free.
    </p>
    <ol>
      <li>Sign into the portal (or create a free account if you do not have one). You land on the Dashboard.</li>
      <li>Click <strong>Packages</strong> in the left sidebar, then <strong>New Package</strong>.</li>
      <li>Upload a document by dragging a PDF or Word file onto the upload area, or click to browse. The portal converts Word documents to PDF automatically.</li>
      <li>Add a signer with your own name and email address. You receive the invitation and complete the ceremony yourself.</li>
      <li>Click on the document preview wherever you want a signature, initial, date, or text field. Fields snap to the page and can be repositioned by dragging. For a faster route, embed text tags directly in the document content. See <a href="/Docs/IntegrationGuides?topic=text-tags">Text Tags</a>.</li>
      <li>Click <strong>Send</strong>. The platform emails the signer an invitation link.</li>
      <li>Open the email you just sent yourself, click the signing link, accept the consent gate, apply your signature, and submit.</li>
      <li>Return to the portal, open the package, and download the signed PDF and Certificate of Completion. Both are emailed to signers automatically.</li>
    </ol>
    <p>
      That is the full platform flow with no code required. For ongoing or high-volume
      use, you probably want the API. The next section produces the same result
      programmatically.
    </p>

    <section class="dev-section">
    <h2>API Quick Start</h2>
    <p>
      The minimal developer path runs four steps. Register a webhook, call one
      create-and-send endpoint, receive the completion event (or poll), and download
      the signed PDF. If any concept is unfamiliar along the way, each step links out
      to its dedicated guide.
    </p>

    <q-banner class="bg-blue-1 text-blue-9 q-mb-lg" rounded>
      <template v-slot:avatar>
        <q-icon name="info" color="blue-9" />
      </template>
      Before you start, you need:
      <ul class="q-mt-sm q-mb-none">
        <li>A <strong>Business tier</strong> subscription. API access requires Business. The portal path above works on every tier including Free.</li>
        <li>An <strong>API credential</strong> from <strong>Settings</strong> then <strong>API</strong>. Click <strong>Generate API Key</strong> and choose either a <strong>Personal Access Token</strong> (one bearer string, paste and go) or <strong>OAuth Client Credentials</strong> (<code>client_id</code> + <code>client_secret</code> exchanged at <code>/connect/token</code>). The plaintext secret is shown once, so store it immediately. See <a href="/Docs/IntegrationGuides?topic=authentication">Authentication &amp; Scopes</a> for the full credential walkthrough and scope selection.</li>
        <li>A <strong>PDF file</strong> to send. If it contains embedded text tags, fields get placed automatically. Otherwise place them via the portal or with additional API calls. See <a href="/Docs/IntegrationGuides?topic=annotations">Signing Fields</a>.</li>
      </ul>
    </q-banner>

    <q-separator class="q-my-lg" />

    <h3>Step 1. Configure a webhook in the portal (optional but recommended)</h3>
    <p>
      Webhooks let your server know the moment something happens, so no polling
      is required. Webhooks are configured in the portal, not via the API. Open
      <strong>Settings</strong> then <strong>Webhooks</strong>, click
      <strong>New Webhook</strong>, enter your server's endpoint URL, select the
      events you care about (at minimum <code>package.completed</code>), and
      save. Copy the signing secret that appears at creation into your server's
      secret store. The same webhook receives events for every future package.
    </p>
    <p>
      If you do not have a receiver wired up yet, skip this step. The polling
      fallback in Step 3 works for local development and first-integration
      testing. See the
      <a href="/Docs/IntegrationGuides?topic=webhooks">Webhooks</a> guide for
      the full payload format and HMAC verification walkthrough once you are
      ready to handle deliveries.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Step 2. Create and send a package</h3>
    <p>
      The <code>create-and-send</code> endpoint does everything in one call. It creates
      the package, attaches your document, adds the signer, and sends the notification
      email immediately.
    </p>
    <p>
      Use your own email address as the signer below. You receive the invitation and
      complete the signing ceremony yourself, so you can watch the whole flow end-to-end
      without needing a second person.
    </p>

    <GuideCodeTabs :samples="samples.createAndSend" />

    <p>The response looks like this:</p>

    <GuideCodeTabs :samples="samples.createAndSendResponse" />

    <p>
      Keep the <code>id</code>. Every subsequent call references the package by it. If
      the document has been processed before (content deduplication), the status reads
      <code>Sent</code> immediately. Otherwise it reads <code>Queued</code> while the
      system renders the PDF. Once processing completes, the package is sent
      automatically.
    </p>

    <q-banner class="bg-amber-1 text-amber-10 q-my-md" rounded>
      <template v-slot:avatar>
        <q-icon name="auto_fix_high" color="amber-10" />
      </template>
      If your PDF contains embedded text tags like
      <code v-pre>{{Signature:1:true:Sign Here:}}</code>, the system auto-detects them
      and places signing fields at those positions with no manual coordinate placement
      needed. See the <a href="/Docs/IntegrationGuides?topic=text-tags">Text Tags</a>
      guide for the full tag syntax.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h3>Step 3. Receive the webhook</h3>
    <p>
      When the signer opens the email, signs the document, and submits, your webhook
      endpoint receives a POST request like this:
    </p>

    <GuideCodeTabs :samples="samples.webhookDelivery" />

    <p>
      Always verify the <code>X-ESign-Signature</code> header using the webhook secret
      you saved in Step 1 before processing the payload. The signed input is
      <code>"{timestamp}.{rawJsonBody}"</code>, not the body alone. Respond with a
      <code>200 OK</code> to acknowledge receipt. See the
      <a href="/Docs/IntegrationGuides?topic=webhooks">Webhooks</a> guide for the full
      HMAC verification walkthrough with working code samples in every language.
    </p>

    <q-banner class="bg-blue-1 text-blue-9 q-my-md" rounded>
      <template v-slot:avatar>
        <q-icon name="refresh" color="blue-9" />
      </template>
      No webhook receiver wired up yet? Poll
      <code>GET /api/v1/packages/{id}</code> every few seconds until
      <code>status === 'Completed'</code>. Polling works for local development and
      short-running scripts. Avoid it in production, because it costs requests against
      the 120 per minute global rate limit and delays your response to completion
      events.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h3>Step 4. Download the signed documents</h3>
    <p>
      Once the package reaches <code>Completed</code>, fetch a time-limited URL for the
      combined signed PDF:
    </p>

    <GuideCodeTabs :samples="samples.download" />

    <p>
      The response contains a time-limited pre-signed <code>url</code>. No API key is
      required to consume it. Download the file immediately or hand the URL to the end
      user. Do not cache it long-term. Request a fresh URL the next time you need to
      download.
    </p>

    <q-separator class="q-my-lg" />

    <h3>What's next</h3>
    <p>You have completed the core signing workflow. Common next steps:</p>
    <ul>
      <li>
        <a href="/Docs/IntegrationGuides?topic=text-tags">Text Tags</a>. Embed tags in
        your document templates for fully automated field placement.
      </li>
      <li>
        <a href="/Docs/IntegrationGuides?topic=embedded-signing">Embedded Signing</a>.
        Generate signing URLs and direct signers to sign from within your own
        application instead of via email.
      </li>
      <li>
        <a href="/Docs/IntegrationGuides?topic=templates">Templates</a>. Create reusable
        package blueprints so one API call produces a ready-to-send package.
      </li>
      <li>
        Multi-step package flow. For more control than <code>create-and-send</code>,
        build a package across separate API calls: create a draft, upload documents,
        add signers, place fields, then send. See
        <a href="/Docs/IntegrationGuides?topic=packages">Packages</a>,
        <a href="/Docs/IntegrationGuides?topic=documents">Documents</a>,
        <a href="/Docs/IntegrationGuides?topic=signers">Signers</a>, and
        <a href="/Docs/IntegrationGuides?topic=annotations">Signing Fields</a>.
      </li>
      <li>
        <a href="/Docs/IntegrationGuides?topic=errors">Errors &amp; Rate Limits</a>.
        If something goes wrong along the way, start here.
      </li>
    </ul>

    <q-banner class="bg-green-1 text-green-9 q-mt-md" rounded>
      <template v-slot:avatar>
        <q-icon name="menu_book" color="green-9" />
      </template>
      See the Packages and Webhooks sections in the
      <a href="/Docs/ApiReference" class="text-green-9" style="text-decoration: underline;">API Reference</a>
      for complete endpoint schemas and try-it-out examples.
    </q-banner>
    </section>
  </div>
</template>

<script>
import GuideCodeTabs from './shared/GuideCodeTabs.vue';

// Code samples live in template literals so newlines and indentation survive Vue's template
// compiler exactly as written. Each entry follows the `samples` prop shape expected by
// GuideCodeTabs: an object whose keys are language ids and values are multi-line strings.
const samples = {
    createWebhook: {
        curl: `curl -X POST https://api.esignlaunchpad.com/api/v1/webhooks \\
  -H "Authorization: Bearer $ESIGN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://yourapp.com/webhooks/esign",
    "events": ["package.completed", "signer.completed"],
    "description": "My first webhook"
  }'`,

        js: `const response = await fetch('https://api.esignlaunchpad.com/api/v1/webhooks', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    url: 'https://yourapp.com/webhooks/esign',
    events: ['package.completed', 'signer.completed'],
    description: 'My first webhook'
  })
});
const webhook = await response.json();
console.log('Signing secret prefix:', webhook.signingSecretPrefix);`,

        node: `// Node 18+ has native fetch. For older versions use node-fetch or axios.
const response = await fetch('https://api.esignlaunchpad.com/api/v1/webhooks', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    url: 'https://yourapp.com/webhooks/esign',
    events: ['package.completed', 'signer.completed'],
    description: 'My first webhook'
  })
});
if (!response.ok) throw new Error(\`\${response.status} \${await response.text()}\`);
const webhook = await response.json();
console.log('Signing secret prefix:', webhook.signingSecretPrefix);`,

        python: `import os
import requests

response = requests.post(
    'https://api.esignlaunchpad.com/api/v1/webhooks',
    headers={
        'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}",
        'Content-Type': 'application/json'
    },
    json={
        'url': 'https://yourapp.com/webhooks/esign',
        'events': ['package.completed', 'signer.completed'],
        'description': 'My first webhook'
    }
)
response.raise_for_status()
webhook = response.json()
print('Signing secret prefix:', webhook['signingSecretPrefix'])`,

        csharp: `using System;
using System.Net.Http;
using System.Net.Http.Json;
using System.Text.Json;

using var client = new HttpClient { BaseAddress = new Uri("https://api.esignlaunchpad.com/") };
client.DefaultRequestHeaders.Authorization = new AuthenticationHeaderValue("Bearer", Environment.GetEnvironmentVariable("ESIGN_TOKEN"));

var payload = new
{
    url = "https://yourapp.com/webhooks/esign",
    events = new[] { "package.completed", "signer.completed" },
    description = "My first webhook"
};

var response = await client.PostAsJsonAsync("api/v1/webhooks", payload);
response.EnsureSuccessStatusCode();
var webhook = await response.Content.ReadFromJsonAsync<JsonElement>();
Console.WriteLine($"Signing secret prefix: {webhook.GetProperty("signingSecretPrefix").GetString()}");`,

        php: `<?php
require 'vendor/autoload.php';

use GuzzleHttp\\Client;

$client = new Client(['base_uri' => 'https://api.esignlaunchpad.com/']);

$response = $client->post('api/v1/webhooks', [
    'headers' => [
        'Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN'),
        'Content-Type' => 'application/json',
    ],
    'json' => [
        'url' => 'https://yourapp.com/webhooks/esign',
        'events' => ['package.completed', 'signer.completed'],
        'description' => 'My first webhook',
    ],
]);

$webhook = json_decode($response->getBody(), true);
echo "Signing secret prefix: {$webhook['signingSecretPrefix']}\\n";`,

        ruby: `require 'net/http'
require 'json'
require 'uri'

uri = URI('https://api.esignlaunchpad.com/api/v1/webhooks')
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = {
  url: 'https://yourapp.com/webhooks/esign',
  events: ['package.completed', 'signer.completed'],
  description: 'My first webhook'
}.to_json

res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |http| http.request(req) }
raise "#{res.code} #{res.body}" unless res.is_a?(Net::HTTPSuccess)

webhook = JSON.parse(res.body)
puts "Signing secret prefix: #{webhook['signingSecretPrefix']}"`
    },

    createAndSend: {
        curl: `# Base64-encode the PDF first (Mac/Linux):
BODY_B64=$(base64 -i agreement.pdf | tr -d '\\n')

# Use a heredoc so the JSON body needs no escaping. -d @- reads from stdin.
curl -X POST https://api.esignlaunchpad.com/api/v1/packages/create-and-send/json \\
  -H "Authorization: Bearer $ESIGN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d @- <<EOF
{
  "name": "Quick Start Test",
  "emailSubject": "Please sign this document",
  "documents": [
    { "fileName": "agreement.pdf", "documentBase64": "$BODY_B64" }
  ],
  "signers": [
    { "email": "signer@example.com", "name": "Jane Doe" }
  ]
}
EOF`,

        js: `// In a browser, read a File from an <input type="file"> and base64-encode it.
async function createAndSend(file) {
  const documentBase64 = await fileToBase64(file);

  const response = await fetch('https://api.esignlaunchpad.com/api/v1/packages/create-and-send/json', {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + ESIGN_TOKEN,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      name: 'Quick Start Test',
      emailSubject: 'Please sign this document',
      documents: [{ fileName: file.name, documentBase64 }],
      signers: [{ email: 'signer@example.com', name: 'Jane Doe' }]
    })
  });
  return response.json();
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}`,

        node: `const fs = require('node:fs/promises');

const pdf = await fs.readFile('agreement.pdf');
const documentBase64 = pdf.toString('base64');

const response = await fetch('https://api.esignlaunchpad.com/api/v1/packages/create-and-send/json', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    name: 'Quick Start Test',
    emailSubject: 'Please sign this document',
    documents: [{ fileName: 'agreement.pdf', documentBase64 }],
    signers: [{ email: 'signer@example.com', name: 'Jane Doe' }]
  })
});
if (!response.ok) throw new Error(\`\${response.status} \${await response.text()}\`);
const pkg = await response.json();
console.log('Package id:', pkg.id, 'status:', pkg.status);`,

        python: `import base64
import os
import requests

with open('agreement.pdf', 'rb') as f:
    document_base64 = base64.b64encode(f.read()).decode()

response = requests.post(
    'https://api.esignlaunchpad.com/api/v1/packages/create-and-send/json',
    headers={
        'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}",
        'Content-Type': 'application/json'
    },
    json={
        'name': 'Quick Start Test',
        'emailSubject': 'Please sign this document',
        'documents': [{'fileName': 'agreement.pdf', 'documentBase64': document_base64}],
        'signers': [{'email': 'signer@example.com', 'name': 'Jane Doe'}]
    }
)
response.raise_for_status()
pkg = response.json()
print(f"Package id: {pkg['id']}, status: {pkg['status']}")`,

        csharp: `using System;
using System.IO;
using System.Net.Http;
using System.Net.Http.Json;
using System.Text.Json;

var pdfBytes = await File.ReadAllBytesAsync("agreement.pdf");
var documentBase64 = Convert.ToBase64String(pdfBytes);

using var client = new HttpClient { BaseAddress = new Uri("https://api.esignlaunchpad.com/") };
client.DefaultRequestHeaders.Authorization = new AuthenticationHeaderValue("Bearer", Environment.GetEnvironmentVariable("ESIGN_TOKEN"));

var payload = new
{
    name = "Quick Start Test",
    emailSubject = "Please sign this document",
    documents = new[] { new { fileName = "agreement.pdf", documentBase64 } },
    signers   = new[] { new { email = "signer@example.com", name = "Jane Doe" } }
};

var response = await client.PostAsJsonAsync("api/v1/packages/create-and-send/json", payload);
response.EnsureSuccessStatusCode();
var pkg = await response.Content.ReadFromJsonAsync<JsonElement>();
Console.WriteLine($"Package id: {pkg.GetProperty("id")}, status: {pkg.GetProperty("status")}");`,

        php: `<?php
require 'vendor/autoload.php';

use GuzzleHttp\\Client;

$documentBase64 = base64_encode(file_get_contents('agreement.pdf'));

$client = new Client(['base_uri' => 'https://api.esignlaunchpad.com/']);
$response = $client->post('api/v1/packages/create-and-send/json', [
    'headers' => [
        'Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN'),
        'Content-Type' => 'application/json',
    ],
    'json' => [
        'name' => 'Quick Start Test',
        'emailSubject' => 'Please sign this document',
        'documents' => [
            ['fileName' => 'agreement.pdf', 'documentBase64' => $documentBase64],
        ],
        'signers' => [
            ['email' => 'signer@example.com', 'name' => 'Jane Doe'],
        ],
    ],
]);

$pkg = json_decode($response->getBody(), true);
echo "Package id: {$pkg['id']}, status: {$pkg['status']}\\n";`,

        ruby: `require 'net/http'
require 'base64'
require 'json'
require 'uri'

document_base64 = Base64.strict_encode64(File.binread('agreement.pdf'))

uri = URI('https://api.esignlaunchpad.com/api/v1/packages/create-and-send/json')
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = {
  name: 'Quick Start Test',
  emailSubject: 'Please sign this document',
  documents: [{ fileName: 'agreement.pdf', documentBase64: document_base64 }],
  signers:   [{ email: 'signer@example.com', name: 'Jane Doe' }]
}.to_json

res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |http| http.request(req) }
raise "#{res.code} #{res.body}" unless res.is_a?(Net::HTTPSuccess)

pkg = JSON.parse(res.body)
puts "Package id: #{pkg['id']}, status: #{pkg['status']}"`
    },

    createAndSendResponse: {
        json: `{
  "id": "550e8400-e29b-41d4-a716-446655440000",
  "name": "Quick Start Test",
  "status": "Sent",
  "createdOn": "2026-04-14T10:30:00Z",
  "totalDocuments": 1,
  "totalSigners": 1
}`
    },

    webhookDelivery: {
        http: `POST /webhooks/esign HTTP/1.1
Host: yourapp.com
Content-Type: application/json
X-ESign-Event: package.completed
X-ESign-Delivery: 7c1d4a2e-5b9f-4c3d-8f2e-1a6b7c9d0e4f
X-ESign-Signature: t=1741879800,v1=a1b2c3d4e5f6...

{
  "tenantId": 42,
  "packageGuid": "550e8400-e29b-41d4-a716-446655440000",
  "packageName": "Quick Start Test",
  "completedOn": "2026-03-01T14:29:58Z",
  "totalSigners": 1,
  "totalAnnotations": 3,
  "timestamp": "2026-03-01T14:30:00Z"
}`
    },

    download: {
        curl: `curl https://api.esignlaunchpad.com/api/v1/packages/$PACKAGE_ID/download \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const response = await fetch(
  \`https://api.esignlaunchpad.com/api/v1/packages/\${packageId}/download\`,
  { headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } }
);
const { url, expiresOn } = await response.json();
// Redirect the user or download server-side. The URL is pre-signed.
window.location.href = url;`,

        node: `const response = await fetch(
  \`https://api.esignlaunchpad.com/api/v1/packages/\${packageId}/download\`,
  { headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } }
);
const { url, expiresOn } = await response.json();

// Stream the signed PDF to disk.
const fs = require('node:fs');
const { pipeline } = require('node:stream/promises');
const pdfRes = await fetch(url);
await pipeline(pdfRes.body, fs.createWriteStream('signed.pdf'));`,

        python: `import os
import requests

response = requests.get(
    f'https://api.esignlaunchpad.com/api/v1/packages/{package_id}/download',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
)
response.raise_for_status()
url = response.json()['url']

with requests.get(url, stream=True) as pdf_resp:
    pdf_resp.raise_for_status()
    with open('signed.pdf', 'wb') as f:
        for chunk in pdf_resp.iter_content(8192):
            f.write(chunk)`,

        csharp: `using System;
using System.IO;
using System.Net.Http;
using System.Net.Http.Json;
using System.Text.Json;

using var client = new HttpClient { BaseAddress = new Uri("https://api.esignlaunchpad.com/") };
client.DefaultRequestHeaders.Authorization = new AuthenticationHeaderValue("Bearer", Environment.GetEnvironmentVariable("ESIGN_TOKEN"));

var linkResponse = await client.GetFromJsonAsync<JsonElement>($"api/v1/packages/{packageId}/download");
var url = linkResponse.GetProperty("url").GetString()!;

await using var pdfStream = await client.GetStreamAsync(url);
await using var fileStream = File.Create("signed.pdf");
await pdfStream.CopyToAsync(fileStream);`,

        php: `<?php
require 'vendor/autoload.php';

use GuzzleHttp\\Client;

$client = new Client(['base_uri' => 'https://api.esignlaunchpad.com/']);

$linkResponse = $client->get("api/v1/packages/{$packageId}/download", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
]);
$link = json_decode($linkResponse->getBody(), true);

// Download the pre-signed URL (no API key required on this one).
file_put_contents('signed.pdf', file_get_contents($link['url']));`,

        ruby: `require 'net/http'
require 'json'
require 'uri'
require 'open-uri'

link_uri = URI("https://api.esignlaunchpad.com/api/v1/packages/#{package_id}/download")
link_req = Net::HTTP::Get.new(link_uri)
link_req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
link_res = Net::HTTP.start(link_uri.hostname, link_uri.port, use_ssl: true) { |h| h.request(link_req) }
raise "#{link_res.code} #{link_res.body}" unless link_res.is_a?(Net::HTTPSuccess)

url = JSON.parse(link_res.body)['url']
URI.open(url) { |src| File.open('signed.pdf', 'wb') { |f| IO.copy_stream(src, f) } }`
    }
};

export default {
    name: 'GuideQuickStart',
    components: { GuideCodeTabs },
    data() {
        return { samples };
    }
};
</script>

<style scoped>
.guide-lead {
    font-size: 1.1rem;
    line-height: 1.7;
    color: #334155;
}
</style>
